<template>
  <div class="position-relative unit-input-wrap" ref="wrap">
    <div class="d-flex mb-1 align-items-center">
      <!-- Multi-mode: show summary display -->
      <div
        v-if="multiModeActive"
        class="form-control flex-1 unit-summary-display"
        @click="openPicker"
        style="cursor: pointer"
      >
        {{ multiSummary || "0" }}
      </div>

      <!-- Normal mode: quantity input + unit select -->
      <template v-else>
        <quantity-input
          class="form-control text-right flex-1 font-14 box-80 mr-1"
          v-model="value"
          @input="onSingleInput"
          :max="max"
          :decimal="decimal"
        />
        <select
          v-if="hasMultipleUnits"
          class="form-control w-70px"
          v-model="unitSelect"
          @change="onChange()"
          style="padding-left: 4px"
        >
          <option v-for="t in sortedUnits" :key="t.name" :value="t.name">
            {{ t.name }}
          </option>
        </select>
      </template>

      <!-- Toggle multi-unit picker button -->
      <button
        v-if="hasMultipleUnits"
        type="button"
        class="btn btn-sm ml-1 btn-multi-toggle"
        :class="{ active: showPicker }"
        @click="togglePicker"
        title="Nhập nhiều đơn vị"
        style="flex-shrink: 0; padding: 4px 8px"
      >
        <i :class="showPicker ? 'fa fa-times' : 'fa fa-edit'"></i>
      </button>
    </div>

    <!-- Base unit total -->
    <!-- <p
      class="sub-text m-0 position-absolute"
      style="font-size: 11px; right: 0; top: 32px"
    >
      {{ $formatQuantity(totalBaseQty) }} {{ baseUnit }}
    </p> -->

    <!-- Multi-unit picker dropdown -->
    <div
      v-if="showPicker"
      ref="picker"
      class="unit-picker"
      :style="pickerStyle"
    >
      <div v-for="u in sortedUnits" :key="u.name" class="unit-picker-row">
        <div class="unit-picker-label mr-2">
          <span>{{ u.name }}</span>
          <small v-if="u.transfer !== 1" class="text-muted">
            1 {{ u.name }} = {{ u.transfer }} {{ baseUnit }}
          </small>
        </div>
        <div class="unit-picker-stepper">
          <button type="button" @click="decUnit(u)">−</button>
          <input
            :value="pickerQtys[u.name] || 0"
            @change="setUnitQty(u.name, $event.target.value)"
            @focus="$event.target.select()"
            type="number"
            min="0"
          />
          <button type="button" @click="incUnit(u)">+</button>
        </div>
      </div>

      <div class="unit-picker-total">
        Tổng: {{ $formatQuantity(pickerTotalBase) }} {{ baseUnit }}
      </div>

      <div class="unit-picker-actions">
        <button
          type="button"
          class="btn btn-sm btn-outline-danger"
          @click="resetPicker"
        >
          Đặt lại
        </button>
        <button
          type="button"
          class="btn btn-sm btn-primary"
          @click="confirmPicker"
        >
          Xác nhận
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import QuantityInput from "./QuantityInput.vue"

export default {
  name: "QuantityUnitInput",
  components: { QuantityInput },
  props: ["exchangeUnits", "quantity", "unit", "decimal", "max"],
  data() {
    return {
      value: this.quantity,
      transfer: 1,
      unitSelect: this.unit,
      showPicker: false,
      multiModeActive: false,
      multiUnitQtys: {},
      pickerQtys: {},
      pickerStyle: {},
    }
  },
  mounted() {
    this.initUnit()
    document.addEventListener("click", this.onDocClick)
    window.addEventListener("resize", this.updatePickerPosition)
    window.addEventListener("scroll", this.updatePickerPosition, true)
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocClick)
    window.removeEventListener("resize", this.updatePickerPosition)
    window.removeEventListener("scroll", this.updatePickerPosition, true)
  },
  computed: {
    sortedUnits() {
      return [...(this.exchangeUnits || [])].sort(
        (a, b) => b.transfer - a.transfer,
      )
    },
    baseUnit() {
      const s = (this.exchangeUnits || []).find((t) => t.transfer == 1)
      return s ? s.name : ""
    },
    hasMultipleUnits() {
      return (this.exchangeUnits || []).length > 1
    },
    totalBaseQty() {
      if (this.multiModeActive) return this.calcBase(this.multiUnitQtys)
      return this.value * this.transfer
    },
    pickerTotalBase() {
      return this.calcBase(this.pickerQtys)
    },
    multiSummary() {
      return this.sortedUnits
        .filter((u) => (this.multiUnitQtys[u.name] || 0) > 0)
        .map(
          (u) =>
            `${this.$formatQuantity(this.multiUnitQtys[u.name])} ${u.name}`,
        )
        .join(" + ")
    },
  },
  methods: {
    decomposeToUnits(baseQty) {
      const units = [...(this.exchangeUnits || [])].sort(
        (a, b) => b.transfer - a.transfer,
      )
      const result = {}
      let remaining = baseQty
      for (const u of units) {
        if (u.transfer > 0 && remaining >= u.transfer) {
          const qty = Math.floor(remaining / u.transfer)
          if (qty > 0) {
            result[u.name] = qty
            remaining = remaining - qty * u.transfer
          }
        }
      }
      return result
    },
    initUnit() {
      const units = this.exchangeUnits || []
      const s =
        units.find((t) => t.name == this.unitSelect) ||
        units.find((t) => t.transfer == 1)
      this.transfer = s ? s.transfer : 1
      this.unitSelect = this.unit || this.baseUnit

      if (units.length > 1 && this.quantity > 0) {
        const decomposed = this.decomposeToUnits(this.quantity)
        const activeCount = Object.values(decomposed).filter(
          (v) => v > 0,
        ).length
        if (activeCount > 1) {
          this.multiUnitQtys = decomposed
          this.multiModeActive = true
          return
        }
      }
      this.value = this.quantity / this.transfer
      this.multiModeActive = false
    },
    calcBase(qtys) {
      return (this.exchangeUnits || []).reduce(
        (sum, u) => sum + (qtys[u.name] || 0) * u.transfer,
        0,
      )
    },
    onSingleInput(e) {
      this.$emit("change", {
        quantity: e * this.transfer,
        unit: this.unitSelect,
        transfer: this.transfer,
      })
    },
    onChange() {
      const units = this.exchangeUnits || []
      const s =
        units.find((t) => t.name == this.unitSelect) ||
        units.find((t) => t.transfer == 1)
      this.transfer = s ? s.transfer : 1
      this.$emit("change", {
        quantity: this.value * this.transfer,
        unit: this.unitSelect,
        transfer: this.transfer,
      })
    },
    togglePicker() {
      if (this.showPicker) {
        this.showPicker = false
      } else {
        this.openPicker()
      }
    },
    openPicker() {
      let qtys
      if (this.multiModeActive) {
        qtys = { ...this.multiUnitQtys }
      } else {
        const baseQty = this.value * this.transfer
        qtys = baseQty > 0 ? this.decomposeToUnits(baseQty) : {}
        if (Object.keys(qtys).length === 0 && this.unitSelect && this.value) {
          qtys = { [this.unitSelect]: this.value }
        }
      }
      this.pickerQtys = qtys
      this.showPicker = true
      this.$nextTick(this.updatePickerPosition)
    },
    updatePickerPosition() {
      if (!this.showPicker || !this.$refs.wrap) return

      const gap = 6
      const margin = 8
      const rect = this.$refs.wrap.getBoundingClientRect()
      const picker = this.$refs.picker
      const pickerHeight = picker ? picker.offsetHeight : 0
      const pickerWidth = picker ? picker.offsetWidth : 250
      const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight

      let top = rect.bottom + gap
      if (
        pickerHeight &&
        top + pickerHeight > viewportHeight - margin &&
        rect.top - pickerHeight - gap >= margin
      ) {
        top = rect.top - pickerHeight - gap
      }

      let left = rect.left
      if (left + pickerWidth > viewportWidth - margin) {
        left = viewportWidth - pickerWidth - margin
      }

      this.pickerStyle = {
        top: `${Math.max(margin, top)}px`,
        left: `${Math.max(margin, left)}px`,
        minWidth: `${Math.max(rect.width, 250)}px`,
      }
    },
    incUnit(u) {
      this.$set(this.pickerQtys, u.name, (this.pickerQtys[u.name] || 0) + 1)
    },
    decUnit(u) {
      const cur = this.pickerQtys[u.name] || 0
      if (cur > 0) this.$set(this.pickerQtys, u.name, cur - 1)
    },
    setUnitQty(name, val) {
      this.$set(this.pickerQtys, name, Math.max(0, parseFloat(val) || 0))
    },
    confirmPicker() {
      const total = this.pickerTotalBase
      const decomposed = this.decomposeToUnits(total)
      const activeNames = Object.keys(decomposed).filter(
        (name) => decomposed[name] > 0,
      )

      this.multiUnitQtys = decomposed

      if (activeNames.length > 1) {
        this.multiModeActive = true
      } else if (activeNames.length === 1) {
        this.multiModeActive = false
        const u = (this.exchangeUnits || []).find(
          (u) => u.name === activeNames[0],
        )
        this.unitSelect = activeNames[0]
        this.transfer = u ? u.transfer : 1
        this.value = decomposed[activeNames[0]]
      } else {
        this.multiModeActive = false
        this.value = 0
      }

      this.showPicker = false

      const dominant =
        activeNames.length === 1
          ? (this.exchangeUnits || []).find((u) => u.name === activeNames[0])
          : (this.exchangeUnits || []).find((u) => u.transfer === 1)

      this.$emit("change", {
        quantity: total,
        unit: dominant ? dominant.name : this.unitSelect,
        transfer: dominant ? dominant.transfer : this.transfer,
        multiUnitQtys: decomposed,
      })
    },
    resetPicker() {
      this.pickerQtys = {}
      this.multiUnitQtys = {}
      this.multiModeActive = false
      this.showPicker = false
      this.initUnit()
      this.$emit("change", {
        quantity: this.value * this.transfer,
        unit: this.unitSelect,
        transfer: this.transfer,
      })
    },
    onDocClick(e) {
      if (
        this.$refs.wrap &&
        !this.$refs.wrap.contains(e.target) &&
        (!this.$refs.picker || !this.$refs.picker.contains(e.target))
      ) {
        this.showPicker = false
      }
    },
  },
}
</script>

<style scoped>
.unit-summary-display {
  display: flex;
  align-items: center;
  font-size: 13px;
  min-height: 38px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.btn-multi-toggle {
  border: 1px solid #ced4da;
  background: #fff;
  color: #495057;
}

.btn-multi-toggle.active {
  background: #007bff;
  border-color: #007bff;
  color: #fff;
}

.unit-picker {
  position: fixed;
  z-index: 1050;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  padding: 12px;
  min-width: 250px;
}

.unit-picker-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.unit-picker-label {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.unit-picker-label span {
  font-size: 14px;
  font-weight: 500;
}

.unit-picker-stepper {
  display: flex;
  align-items: center;
  gap: 4px;
}

.unit-picker-stepper button {
  width: 28px;
  height: 28px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #f8f9fa;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  padding: 0;
}

.unit-picker-stepper button:hover {
  background: #e9ecef;
}

.unit-picker-stepper input {
  width: 56px;
  height: 28px;
  border: 1px solid #ccc;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
  -moz-appearance: textfield;
}

.unit-picker-stepper input::-webkit-inner-spin-button,
.unit-picker-stepper input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  appearance: none;
}

.unit-picker-total {
  padding: 8px 0 4px;
  font-size: 12px;
  color: #666;
  text-align: right;
}

.unit-picker-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}
</style>
